import { SaveToLibrary } from "./SaveToLibrary";
import { DraftingControls } from "./DraftingControls";
import { draftingLabels } from "../shared/drafting";
import { workspaceBottomInset } from "./workspace-layout";
import { useEffect, useRef, useState } from "react";
import { StickyFormatting } from "./StickyFormatting";
import { WebLinkControls } from "./WebLinkControls";
import { DrawingControls } from "./DrawingControls";
import {
  Unlock,
  SlidersHorizontal,
  ExternalLink,
  Play,
  Sparkles,
} from "lucide-react";
import { useValue } from "@tldraw/state-react";
import type { WorkspaceModel } from "./useWorkspace";
import { TextFormatting } from "./TextFormatting";
import { nativeDocument } from "../shared/document-session";
import { MakeThreeDButton } from "./MakeThreeD";

/** Everyday actions stay beside the object. Detailed properties are opt-in. */
export function SelectionToolbar({
  model,
  onProperties,
  onSketch,
}: {
  model: WorkspaceModel;
  onProperties: (id: string) => void;
  onSketch?: () => void;
}) {
  const { editor, selected } = model;
  const root = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 440, height: 44 });
  useEffect(() => {
    if (!root.current) return;
    const observer = new ResizeObserver(([entry]) =>
      setSize({
        width: Math.ceil(
          entry.borderBoxSize[0]?.inlineSize || entry.contentRect.width + 12,
        ),
        height: Math.ceil(
          entry.borderBoxSize[0]?.blockSize || entry.contentRect.height + 12,
        ),
      }),
    );
    observer.observe(root.current);
    return () => observer.disconnect();
  }, [selected.map((s) => s.id).join(",")]);
  const state = useValue(
    "selection toolbar",
    () => {
      const bounds = editor.getSelectionPageBounds();
      if (!bounds || !bounds.collides(editor.getViewportPageBounds()))
        return null;
      const view = editor.getViewportScreenBounds(),
        top = editor.pageToScreen({ x: bounds.center.x, y: bounds.minY });
      const width = Math.min(size.width, view.w - 32);
      const height = size.height;
      const bottom = editor.pageToScreen({
        x: bounds.center.x,
        y: bounds.maxY,
      });
      const above = top.y - view.y - height - 12;
      const below = bottom.y - view.y + 12;
      return {
        left: Math.max(
          16,
          Math.min(view.w - width - 16, top.x - view.x - width / 2),
        ),
        top: Math.max(
          142,
          Math.min(
            view.h - height - workspaceBottomInset(view.w),
            above >= 142 ? above : below,
          ),
        ),
        width: "max-content" as const,
        maxWidth: view.w - 32,
      };
    },
    [editor, size],
  );
  const s = selected.length === 1 ? selected[0] : undefined;
  const locked = selected.some((shape) =>
    editor.isShapeOrAncestorLocked(shape),
  );
  const imported =
    s && !nativeDocument(s) && ["image", "document", "table"].includes(s.type);
  if (
    !state ||
    !selected.length ||
    nativeDocument(editor.getEditingShape()) ||
    (imported && !locked) ||
    (s?.type === "trace" && !locked) ||
    model.pickingTrace ||
    model.annotation.trace ||
    model.modal ||
    model.cropping ||
    // A new comment on the selection (Cmd+K) sits where the toolbar would.
    model.pending ||
    (!editor.isIn("select") &&
      !(s?.type === "text" && editor.getEditingShapeId() === s.id))
  )
    return null;
  const url =
    s &&
    (s.type === "bookmark" || s.type === "embed") &&
    /^https?:\/\//i.test(s.props.url)
      ? s.props.url
      : null;
  return (
    <div
      ref={root}
      className={`selection-object-toolbar floating-surface${s?.type === "text" && model.canEdit && !locked ? " text-object-toolbar" : ""}`}
      style={state}
      aria-label="Selection tools"
      onKeyDown={(event) => {
        // React also bubbles events from portaled menus through this component.
        // Escape in one of those menus belongs to the menu, not the selection.
        if (
          event.key !== "Escape" ||
          event.defaultPrevented ||
          !event.currentTarget.contains(event.target as Node)
        )
          return;
        event.preventDefault();
        event.stopPropagation();
        editor.selectNone();
        editor.focus();
      }}
    >
      <div
        className={`selection-toolbar-row${s?.type === "text" ? " selection-toolbar-text" : ""}`}
      >
        {s?.type === "text" && model.canEdit && !locked && (
          <TextFormatting editor={editor} shape={s} />
        )}
        {(s?.type !== "text" || locked || !model.canEdit) && (
          <strong>
            {s?.type === "sheet"
              ? s.props.name
              : s?.type === "bookmark"
                ? "Web link"
                : s?.type === "embed"
                  ? "Embedded link"
                  : selected.length > 1
                    ? `${selected.length} selected`
                    : locked
                      ? "Locked object"
                      : s?.type === "mark"
                        ? s.meta.polygon
                          ? "Polygon"
                          : (
                              {
                                line: "Line",
                                dimension: "Dimension",
                                area: "Area",
                                angle: "Angle",
                              } as Record<string, string>
                            )[s.props.kind] || "Markup"
                        : s?.type === "drafting"
                          ? draftingLabels[s.props.kind]
                          : s?.type === "draw"
                            ? "Freehand"
                            : s?.type === "geo"
                              ? "Shape"
                              : s?.type === "arrow"
                                ? "Arrow"
                                : s?.type === "highlight"
                                  ? "Highlighter"
                                  : "Selection"}
          </strong>
        )}
        {selected.length === 1 && <MakeThreeDButton model={model} shape={s} />}
        {model.canEdit && !locked && s?.type === "note" && (
          <StickyFormatting editor={editor} shape={s} />
        )}
        {s && (s.type === "bookmark" || s.type === "embed") && (
          <WebLinkControls editor={editor} shape={s} />
        )}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open link"
          >
            <ExternalLink size={17} /> Open
          </a>
        )}
        {s?.type === "embed" && editor.getEditingShapeId() !== s.id && (
          <button
            onClick={() => {
              editor.setEditingShape(s.id);
              editor.focus();
            }}
          >
            <Play size={17} /> Interact
          </button>
        )}
        <SaveToLibrary model={model} />
        {!locked && <DrawingControls model={model} compact />}
        {!locked && <DraftingControls model={model} />}
        {model.canEdit && locked && (
          <button
            aria-label="Unlock selection"
            title="Unlock"
            onClick={() => {
              editor.markHistoryStoppingPoint("Unlock");
              const ids = editor.getSelectedShapeIds();
              editor.toggleLock(ids);
              editor.select(...ids);
            }}
          >
            <Unlock size={17} />
          </button>
        )}
        {onSketch && !locked && (
          <button
            onClick={onSketch}
            title="Create from sketch"
            aria-label="Create from sketch"
          >
            <Sparkles size={17} />
          </button>
        )}
        {!locked && (
          <button
            aria-label="Selection properties"
            title="Properties"
            onClick={(event) => {
              event.currentTarget.focus();
              onProperties(selected.map((s) => s.id).join(","));
            }}
          >
            <SlidersHorizontal size={17} />
          </button>
        )}
      </div>
      {locked && <small>Locked to prevent changes. Unlock here to edit.</small>}
    </div>
  );
}
